import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  DEFAULT_ROOM_CONFIG,
  createDefaultRoomConfig,
} from "../constants/defaults";
import { createDebouncedStorage } from "./helpers";

// 🏠 Room Store (persisted to localStorage to maintain room configuration across page refreshes)
export const useRoomStore = create(
  persist(
    (set) => ({
      scaleX: DEFAULT_ROOM_CONFIG.scaleX,
      scaleZ: DEFAULT_ROOM_CONFIG.scaleZ,
      height: DEFAULT_ROOM_CONFIG.height,
      setScaleX: (val) => set({ scaleX: val }),
      setScaleZ: (val) => set({ scaleZ: val }),
      setHeight: (val) => set({ height: val }),

      materialType: DEFAULT_ROOM_CONFIG.materialType,
      materialId: DEFAULT_ROOM_CONFIG.materialId,
      materialOptions: {},
      shaderOptions: {},

      setMaterialType: (type) => set({ materialType: type }),
      setMaterialId: (id) => set({ materialId: id }),
      setMaterialOptions: (materialOptions) => set({ materialOptions }),
      setShaderOptions: (shaderOptions) => set({ shaderOptions }),
      // Decorations state
      decorations: [],
      setDecorations: (decorations) => set({ decorations }),
      addDecoration: (decoration) =>
        set((state) => ({
          decorations: [...state.decorations, decoration],
        })),
      removeDecoration: (idx) =>
        set((state) => ({
          decorations: state.decorations.filter((_, i) => i !== idx),
        })),
      editDecoration: (index, newProps) =>
        set((state) => {
          const updated = [...state.decorations];
          updated[index] = { ...updated[index], ...newProps };
          return { decorations: updated };
        }),
      updateDecorationProperty: (index, key, value) =>
        set((state) => {
          const updated = [...state.decorations];
          if (!updated[index]) return state;
          updated[index] = { ...updated[index], [key]: value };
          return { decorations: updated };
        }),

      // Room settings (for publish step)
      lightIntensity: DEFAULT_ROOM_CONFIG.lightIntensity,
      setLightIntensity: (val) => set({ lightIntensity: val }),

      roomLiningVisible: DEFAULT_ROOM_CONFIG.roomLiningVisible,
      setRoomLiningVisible: (val) => set({ roomLiningVisible: val }),

      // Creator metadata (not persisted — set per room load)
      creatorUsername: null,
      setCreatorUsername: (val) => set({ creatorUsername: val }),

      // Reset to default state
      resetRoom: () => set({ ...createDefaultRoomConfig(), creatorUsername: null }),
    }),
    {
      name: "room-store", // unique name for localStorage key
      // Debounced storage to prevent UI jank during rapid updates
      storage: createDebouncedStorage(localStorage, 500),
      // Only persist the actual room configuration data, not the setter functions
      partialize: (state) => ({
        scaleX: state.scaleX,
        scaleZ: state.scaleZ,
        height: state.height,
        materialType: state.materialType,
        materialId: state.materialId,
        materialOptions: state.materialOptions,
        shaderOptions: state.shaderOptions,
        decorations: state.decorations,
      }),
    }
  )
);
